import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { LogoFull } from '../context/TenantContext';
import TopoAnimation from '../components/TopoAnimation';

function friendlyAuthError(message) {
  const text = String(message || '');
  if (/PKCE code verifier.*not found|code verifier.*not found|Unable to exchange external code|invalid_grant|authorization code.*invalid|authorization code.*expired/i.test(text)) {
    return 'Your sign-in session expired. Please start Google sign-in again in this browser.';
  }
  if (/admin invite required|invite required|approved account/i.test(text)) {
    return 'This app is invite-only. Ask an admin to add your account before signing in.';
  }
  return text;
}

export default function Login() {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const { signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const authError = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return friendlyAuthError(params.get('auth_error') || params.get('error_description') || params.get('error') || '');
  }, [location.search]);
  const displayError = error || authError;

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    if (location.search) navigate('/login', { replace: true });
    try {
      // signInWithOAuth triggers a full-page redirect to Google;
      // we won't reach the success branch on a healthy flow. The
      // error case (provider disabled, mis-configured client) lands
      // here with an error message so the user sees what went wrong.
      const result = await signInWithGoogle();
      if (!result.success) {
        setError(result.error || 'Google sign-in failed.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-bg-effects">
        <TopoAnimation />
        <div className="bg-orb orb-1" />
        <div className="bg-orb orb-2" />
        <div className="bg-orb orb-3" />
      </div>
      <div className="auth-card">
        <div className="auth-brand">
          <LogoFull size={72} />
        </div>

        <div className="auth-heading">
          <h1>Welcome back</h1>
          <p>Sign in with the Google account an admin invited to access the Paraclete Oil platform.</p>
        </div>

        {displayError && <div className="auth-error">{displayError}</div>}

        {/* Google OAuth — the only login path for the application.
            The button triggers a full-page redirect via Supabase's
            hosted flow. */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="auth-google"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          {loading ? 'Redirecting…' : 'Continue with Google'}
        </button>
      </div>
    </div>
  );
}
